import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const DisneyCruiseLineForm = ({ onSave, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: 'Disney Cruise Line',
    ship_name: '',
    cruise_length: '7-Night',
    departure_date: '',
    return_date: '',
    departure_port: '',
    departure_port_other: '',
    destination: '',
    itinerary: '',
    stateroom_category: '',
    stateroom_number: '',
    deck: '',
    stateroom_location: '',
    dining_rotation: '',
    dining_time: '',
    port_adventures: '',
    port_adventures_price: 0,
    onboard_activities: '',
    character_experiences: '',
    adult_exclusive_areas: '',
    kids_clubs: '',
    spa_services: '',
    spa_services_price: 0,
    specialty_dining: '',
    specialty_dining_price: 0,
    beverage_package: 'Not Included',
    wifi_package: 'Not Included',
    wifi_package_price: 0,
    photo_package: 'Not Included',
    photo_package_price: 0,
    gratuities: 'Included',
    gratuities_price: 0,
    travel_protection: 'Not Included',
    travel_protection_price: 0,
    total_price: 0,
    total_price_due_by: '',
    deposit_due: 0,
    deposit_due_by: '',
    final_payment_due: '',
    special_requests: '',
    celebration_details: '',
    accessibility_needs: '',
    children: [],
    isOption: false,
    optionDescription: '',
    photos: [],
    documents: [],
    ...initialData
  });

  const [errors, setErrors] = useState({});

  // Disney Cruise Line Ships
  const shipOptions = [
    'Disney Magic',
    'Disney Wonder', 
    'Disney Dream',
    'Disney Fantasy',
    'Disney Wish',
    'Disney Treasure',
    'Disney Destiny',
    'Disney Adventure'
  ];

  const cruiseLengthOptions = [
    '1-Night',
    '2-Night',
    '3-Night',
    '4-Night',
    '5-Night',
    '6-Night',
    '7-Night',
    '8-Night',
    '9-Night',
    '10-Night',
    '11-Night',
    '12-Night',
    '14-Night'
  ];

  const departurePortOptions = [
    'Port Canaveral, Florida',
    'Miami, Florida',
    'Fort Lauderdale, Florida',
    'San Diego, California',
    'Galveston, Texas',
    'New York, New York',
    'Vancouver, Canada',
    'Barcelona, Spain',
    'Copenhagen, Denmark',
    'Dover, England',
    'Other (Please Specify)'
  ];

  const destinationOptions = [
    'Bahamas',
    'Caribbean - Western',
    'Caribbean - Eastern', 
    'Caribbean - Southern',
    'Alaska',
    'Mediterranean',
    'Northern Europe/Baltic',
    'Transatlantic',
    'Panama Canal',
    'Hawaii',
    'Mexican Riviera',
    'Canada/New England'
  ];

  const stateroomCategories = [
    'Inside Stateroom',
    'Oceanview Stateroom',
    'Verandah Stateroom',
    'Deluxe Oceanview Stateroom with Navigator\'s Verandah',
    'Deluxe Family Oceanview Stateroom',
    'Deluxe Oceanview Stateroom',
    'Concierge Family Oceanview Stateroom with Verandah',
    'Concierge Oceanview Stateroom with Verandah',
    '1-Bedroom Concierge Suite with Verandah',
    '2-Bedroom Concierge Suite with Verandah',
    'Concierge Royal Suite with Verandah'
  ];

  const diningRotationOptions = [
    'Main Dining (3 restaurants)',
    'Animator\'s Palate',
    'Triton\'s/Tiana\'s Place',
    'Rapunzel\'s Royal Table',
    'Enchanted Garden',
    'Royal Palace/Royal Court',
    'Lumiere\'s',
    'Parrot Cay',
    'Carioca\'s',
    'Custom Rotation'
  ];

  const diningTimeOptions = [
    '5:45 PM (Main Dining)',
    '8:15 PM (Second Dining)'
  ];

  const beveragePackageOptions = [
    'Not Included',
    'Non-Alcoholic Beverage Package',
    'Beer and Wine Package', 
    'Premium Beverage Package',
    'Platinum Beverage Package'
  ];

  const wifiPackageOptions = [
    'Not Included',
    'Connect',
    'Premium',
    'Premium Plus'
  ];

  const photoPackageOptions = [
    'Not Included',
    'Shutters Photo Package',
    'Shutters Premium Package'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { age: '' }]
    }));
  };

  const removeChild = (index) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChild = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, i) => 
        i === index ? { ...child, [field]: value } : child
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.ship_name) newErrors.ship_name = 'Ship name is required';
    if (!formData.departure_date) newErrors.departure_date = 'Departure date is required';
    if (!formData.departure_port) newErrors.departure_port = 'Departure port is required';
    if (formData.departure_port === 'Other (Please Specify)' && !formData.departure_port_other) {
      newErrors.departure_port_other = 'Please specify the departure port';
    }
    if (!formData.destination) newErrors.destination = 'Destination is required';
    if (!formData.stateroom_category) newErrors.stateroom_category = 'Stateroom category is required';
    if (!formData.total_price || formData.total_price <= 0) newErrors.total_price = 'Total price is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Disney Cruise Line form submitted with data:', formData);
    
    if (validateForm()) {
      const componentData = {
        type: 'disney-cruise-line',
        title: formData.title,
        price: formData.total_price,
        isOption: formData.isOption,
        optionDescription: formData.optionDescription,
        paymentTiming: formData.paymentTiming,
        optionalDepositAmount: formData.optionalDepositAmount,
        photos: formData.photos,
        documents: formData.documents,
        data: {
          ship_name: formData.ship_name,
          cruise_length: formData.cruise_length,
          departure_date: formData.departure_date,
          return_date: formData.return_date,
          departure_port: formData.departure_port,
          departure_port_other: formData.departure_port_other,
          destination: formData.destination,
          itinerary: formData.itinerary,
          stateroom_category: formData.stateroom_category,
          stateroom_number: formData.stateroom_number,
          deck: formData.deck,
          stateroom_location: formData.stateroom_location,
          dining_rotation: formData.dining_rotation,
          dining_time: formData.dining_time,
          port_adventures: formData.port_adventures,
          port_adventures_price: formData.port_adventures_price,
          onboard_activities: formData.onboard_activities,
          character_experiences: formData.character_experiences,
          adult_exclusive_areas: formData.adult_exclusive_areas,
          kids_clubs: formData.kids_clubs,
          spa_services: formData.spa_services,
          spa_services_price: formData.spa_services_price,
          specialty_dining: formData.specialty_dining,
          specialty_dining_price: formData.specialty_dining_price,
          beverage_package: formData.beverage_package,
          wifi_package: formData.wifi_package,
          wifi_package_price: formData.wifi_package_price,
          photo_package: formData.photo_package,
          photo_package_price: formData.photo_package_price,
          gratuities: formData.gratuities,
          gratuities_price: formData.gratuities_price,
          travel_protection: formData.travel_protection,
          travel_protection_price: formData.travel_protection_price,
          total_price: formData.total_price,
          total_price_due_by: formData.total_price_due_by,
          deposit_due: formData.deposit_due,
          deposit_due_by: formData.deposit_due_by,
          final_payment_due: formData.final_payment_due,
          special_requests: formData.special_requests,
          celebration_details: formData.celebration_details,
          accessibility_needs: formData.accessibility_needs,
          children: formData.children
        }
      };
      console.log('Calling onSave with:', componentData);
      onSave(componentData);
    } else {
      console.log('Form validation failed with errors:', errors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form disney-cruise-form">
      <div className="form-section">
        <h4>Cruise Details</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Package Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Disney Ship *</label>
            <select
              name="ship_name"
              value={formData.ship_name}
              onChange={handleInputChange}
              className={`form-control ${errors.ship_name ? 'error' : ''}`}
            >
              <option value="">Select Ship</option>
              {shipOptions.map(ship => (
                <option key={ship} value={ship}>{ship}</option>
              ))}
            </select>
            {errors.ship_name && <span className="error-message">{errors.ship_name}</span>}
          </div>
          <div className="form-group">
            <label>Cruise Length</label>
            <select
              name="cruise_length"
              value={formData.cruise_length}
              onChange={handleInputChange}
              className="form-control"
            >
              {cruiseLengthOptions.map(length => (
                <option key={length} value={length}>{length}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Departure Date *</label>
            <input
              type="date"
              name="departure_date"
              value={formData.departure_date}
              onChange={handleInputChange}
              className={`form-control ${errors.departure_date ? 'error' : ''}`}
            />
            {errors.departure_date && <span className="error-message">{errors.departure_date}</span>}
          </div>
          <div className="form-group">
            <label>Return Date</label>
            <input
              type="date"
              name="return_date"
              value={formData.return_date}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Departure Port *</label>
            <select
              name="departure_port"
              value={formData.departure_port}
              onChange={handleInputChange}
              className={`form-control ${errors.departure_port ? 'error' : ''}`}
            >
              <option value="">Select Departure Port</option>
              {departurePortOptions.map(port => (
                <option key={port} value={port}>{port}</option>
              ))}
            </select>
            {errors.departure_port && <span className="error-message">{errors.departure_port}</span>}
          </div>
          {formData.departure_port === 'Other (Please Specify)' && (
            <div className="form-group">
              <label>Specify Departure Port *</label>
              <input
                type="text"
                name="departure_port_other"
                value={formData.departure_port_other}
                onChange={handleInputChange}
                placeholder="e.g., Southampton (London), UK"
                className={`form-control ${errors.departure_port_other ? 'error' : ''}`}
              />
              {errors.departure_port_other && <span className="error-message">{errors.departure_port_other}</span>}
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Destination *</label>
            <select
              name="destination"
              value={formData.destination}
              onChange={handleInputChange}
              className={`form-control ${errors.destination ? 'error' : ''}`}
            >
              <option value="">Select Destination</option>
              {destinationOptions.map(dest => (
                <option key={dest} value={dest}>{dest}</option>
              ))}
            </select>
            {errors.destination && <span className="error-message">{errors.destination}</span>}
          </div>
        </div>

        <div className="form-group">
          <label>Itinerary Details</label>
          <textarea
            name="itinerary"
            value={formData.itinerary}
            onChange={handleInputChange}
            placeholder="Port stops, sea days, special events..."
            className="form-control"
            rows="3"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Stateroom Details</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Stateroom Category *</label>
            <select
              name="stateroom_category"
              value={formData.stateroom_category}
              onChange={handleInputChange}
              className={`form-control ${errors.stateroom_category ? 'error' : ''}`}
            >
              <option value="">Select Stateroom Category</option>
              {stateroomCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            {errors.stateroom_category && <span className="error-message">{errors.stateroom_category}</span>}
          </div>
          <div className="form-group">
            <label>Stateroom Number</label>
            <input
              type="text"
              name="stateroom_number"
              value={formData.stateroom_number}
              onChange={handleInputChange}
              placeholder="e.g., 8036"
              className="form-control"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Deck</label>
            <input
              type="text"
              name="deck"
              value={formData.deck}
              onChange={handleInputChange}
              placeholder="e.g., Deck 8"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Stateroom Location</label>
            <select
              name="stateroom_location"
              value={formData.stateroom_location}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select Location</option>
              <option value="Forward">Forward</option>
              <option value="Midship">Midship</option>
              <option value="Aft">Aft</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Dining & Experiences</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Dining Rotation</label>
            <select
              name="dining_rotation"
              value={formData.dining_rotation}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select Dining Rotation</option>
              {diningRotationOptions.map(rotation => (
                <option key={rotation} value={rotation}>{rotation}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Main Dining Time</label>
            <select
              name="dining_time"
              value={formData.dining_time}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="">Select Dining Time</option>
              {diningTimeOptions.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Port Adventures</label>
            <textarea
              name="port_adventures"
              value={formData.port_adventures}
              onChange={handleInputChange}
              placeholder="Castaway Cay, snorkeling, cultural tours..."
              className="form-control"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Port Adventures Price</label>
            <input
              type="number"
              name="port_adventures_price"
              value={formData.port_adventures_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Onboard Activities</label>
          <textarea
            name="onboard_activities"
            value={formData.onboard_activities}
            onChange={handleInputChange}
            placeholder="AquaDuck, pools, deck parties, shows..."
            className="form-control"
            rows="2"
          />
        </div>

        <div className="form-group">
          <label>Character Experiences</label>
          <textarea
            name="character_experiences"
            value={formData.character_experiences}
            onChange={handleInputChange}
            placeholder="Character meet & greets, dining experiences..."
            className="form-control"
            rows="2"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Family Features</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Adult Exclusive Areas</label>
            <textarea
              name="adult_exclusive_areas"
              value={formData.adult_exclusive_areas}
              onChange={handleInputChange}
              placeholder="Quiet Cove Pool, Senses Spa, adult dining..."
              className="form-control"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Kids Clubs</label>
            <textarea
              name="kids_clubs"
              value={formData.kids_clubs}
              onChange={handleInputChange}
              placeholder="Oceaneer Club, Oceaneer Lab, Edge, Vibe..."
              className="form-control"
              rows="2"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Premium Services</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Spa Services</label>
            <textarea
              name="spa_services"
              value={formData.spa_services}
              onChange={handleInputChange}
              placeholder="Senses Spa treatments, salon services..."
              className="form-control"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Spa Services Price</label>
            <input
              type="number"
              name="spa_services_price"
              value={formData.spa_services_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Specialty Dining</label>
            <textarea
              name="specialty_dining"
              value={formData.specialty_dining}
              onChange={handleInputChange}
              placeholder="Palo, Remy, Enchanté..."
              className="form-control"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Specialty Dining Price</label>
            <input
              type="number"
              name="specialty_dining_price"
              value={formData.specialty_dining_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Add-On Packages</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Beverage Package</label>
            <select
              name="beverage_package"
              value={formData.beverage_package}
              onChange={handleInputChange}
              className="form-control"
            >
              {beveragePackageOptions.map(pkg => (
                <option key={pkg} value={pkg}>{pkg}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>WiFi Package</label>
            <select
              name="wifi_package"
              value={formData.wifi_package}
              onChange={handleInputChange}
              className="form-control"
            >
              {wifiPackageOptions.map(pkg => (
                <option key={pkg} value={pkg}>{pkg}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>WiFi Package Price</label>
            <input
              type="number"
              name="wifi_package_price"
              value={formData.wifi_package_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Photo Package</label>
            <select
              name="photo_package"
              value={formData.photo_package}
              onChange={handleInputChange}
              className="form-control"
            >
              {photoPackageOptions.map(pkg => (
                <option key={pkg} value={pkg}>{pkg}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Photo Package Price</label>
            <input
              type="number"
              name="photo_package_price"
              value={formData.photo_package_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Gratuities</label>
            <select
              name="gratuities"
              value={formData.gratuities}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Included">Included</option>
              <option value="Not Included">Not Included</option>
              <option value="Prepaid">Prepaid</option>
            </select>
          </div>
          <div className="form-group">
            <label>Gratuities Price</label>
            <input
              type="number"
              name="gratuities_price"
              value={formData.gratuities_price}
              onChange={handleInputChange}
              placeholder="0.00"
              className="form-control"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Travel Protection</label>
            <select
              name="travel_protection"
              value={formData.travel_protection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Not Included">Not Included</option>
              <option value="Included">Included</option>
            </select>
          </div>
          {formData.travel_protection === 'Included' && (
            <div className="form-group">
              <label>Travel Protection Price</label>
              <input
                type="number"
                name="travel_protection_price"
                value={formData.travel_protection_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="form-control"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h4>Pricing & Payment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Total Price *</label>
            <input
              type="number"
              name="total_price"
              value={formData.total_price}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className={`form-control ${errors.total_price ? 'error' : ''}`}
            />
            {errors.total_price && <span className="error-message">{errors.total_price}</span>}
          </div>
          <div className="form-group">
            <label>Total Due By</label>
            <input
              type="date"
              name="total_price_due_by"
              value={formData.total_price_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Deposit Due</label>
            <input
              type="number"
              name="deposit_due"
              value={formData.deposit_due}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Deposit Due By</label>
            <input
              type="date"
              name="deposit_due_by"
              value={formData.deposit_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Final Payment Due</label>
          <input
            type="date"
            name="final_payment_due"
            value={formData.final_payment_due}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Special Requests & Celebrations</h4>
        <div className="form-group">
          <label>Special Requests</label>
          <textarea
            name="special_requests"
            value={formData.special_requests}
            onChange={handleInputChange}
            placeholder="Dietary restrictions, room requests, accessibility needs..."
            className="form-control"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>Celebration Details</label>
          <textarea
            name="celebration_details"
            value={formData.celebration_details}
            onChange={handleInputChange}
            placeholder="Birthday, anniversary, honeymoon, special occasions..."
            className="form-control"
            rows="2"
          />
        </div>

        <div className="form-group">
          <label>Accessibility Needs</label>
          <textarea
            name="accessibility_needs"
            value={formData.accessibility_needs}
            onChange={handleInputChange}
            placeholder="Wheelchair accessible stateroom, hearing assistance..."
            className="form-control"
            rows="2"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Children Information</h4>
        <div className="children-section">
          {formData.children.map((child, index) => (
            <div key={index} className="child-entry">
              <div className="form-row">
                <div className="form-group">
                  <label>Child {index + 1} Age</label>
                  <input
                    type="number"
                    value={child.age}
                    onChange={(e) => updateChild(index, 'age', e.target.value)}
                    min="0"
                    max="17"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <button
                    type="button"
                    onClick={() => removeChild(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addChild}
            className="btn btn-secondary btn-sm"
          >
            Add Child
          </button>
        </div>
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional Disney cruise upgrade or cabin category"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for Optional Component</label>
              <select
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Full Price When Selected (e.g., cruise excursions)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay with Final Payment (nothing due initially)</option>
                <option value="bundled">Already Included in Package (no additional payment)</option>
              </select>
              <small className="form-help">
                Choose when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Optional Component Deposit Amount ($)</label>
                <input
                  type="number"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                />
                <small className="form-help">
                  Amount due when client selects this option (leave blank for full price)
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData(prev => ({ ...prev, photos }))}
        onDocumentsChange={(documents) => setFormData(prev => ({ ...prev, documents }))}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save Disney Cruise
        </button>
      </div>
    </form>
  );
};

export default DisneyCruiseLineForm;
